import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { css, type Theme } from '@emotion/react';
import * as RadixDropdownMenu from '@radix-ui/react-dropdown-menu';
import { ListItemFigure, type ListItemFigureProps } from 'client/components/Other/ListItemFigure';
import { ListItemText } from 'client/components/Other/ListItemText';
import { borderRadius } from 'client/Theme';

export interface DropdownMenuItemProps extends Omit<RadixDropdownMenu.DropdownMenuItemProps, 'onSelect'> {
  id: string;
  title: string;
  subtitle?: JSX.Element | string;
  icon?: JSX.Element;
  ornament?: JSX.Element;
  image?: ListItemFigureProps['image'];
  href?: string;
  external?: boolean;
  target?: string;
  component?: JSX.Element;
  onSelect?: (id: string) => void;
}

export const DropdownMenuItem = ({
  id,
  title,
  subtitle,
  icon,
  ornament,
  image,
  href,
  external,
  target = '_blank',
  component = <RadixDropdownMenu.Item />,
  onSelect,
  children,
  ...props
}: DropdownMenuItemProps) => {
  const handleSelect = useCallback(() => !!onSelect && onSelect(id), [id, onSelect]);

  const item = (
    <component.type css={itemStyle} onSelect={handleSelect} {...props}>
      <ListItemFigure icon={icon} ornament={ornament} image={image} size={24} />
      <ListItemText primary={title} secondary={subtitle} />
      {children}
    </component.type>
  );

  if (href) {
    if (external) {
      return (
        <a href={href as string} target={target} css={linkStyle}>
          {item}
        </a>
      );
    }
    return (
      <Link to={href} css={linkStyle}>
        {item}
      </Link>
    );
  }

  return item;
};

const itemStyle = (theme: Theme) => css`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 6px;
  padding-right: 16px;
  border-radius: ${borderRadius}px;
  user-select: none;
  cursor: pointer;

  :focus-visible {
    outline: 4px solid ${theme.color.primary[100]};
    outline-offset: 4px;
  }

  :not([data-disabled]):hover {
    background-color: ${theme.color.grey[60]};
    outline: none;
  }

  &[data-disabled] {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const linkStyle = css`
  text-decoration: none;
`;
